import { mountRootParcel, type Parcel } from 'single-spa';
import { useEffect, useRef, type CSSProperties } from 'react';

interface ContentStudioContext {
  source?: string;
  advertiserId?: string;
  eventId?: string;
}

interface ContentStudioOverlayProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user?: any;
  context?: ContentStudioContext;
  onClose: () => void;
}

const mountContainer: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
};

/**
 * Mounts the `content-studio` federated remote as a single-spa parcel in-place.
 * The host page underneath stays mounted, so its state is preserved.
 * McModalNavigator inside the remote owns the modal chrome; this wrapper only
 * provides a full-viewport mount point.
 *
 * We mount the parcel manually via `mountRootParcel` (rather than `single-spa-react/parcel`)
 * because React 18 `StrictMode` double-invokes mount/unmount in dev, which races the
 * `<Parcel>` component's async lifecycle and leaves the parcel torn down (blank panel).
 * The `cancelled` guard makes the StrictMode mount→unmount→mount cycle safe.
 */
export function ContentStudioOverlay({ user, context, onClose }: ContentStudioOverlayProps) {
  const mountElRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = mountElRef.current;
    if (!el) return;

    let parcel: Parcel | null = null;
    let cancelled = false;

    import('content-studio/App')
      .then((lifecycles) => {
        if (cancelled) return undefined;
        parcel = mountRootParcel(lifecycles as never, {
          domElement: el,
          user,
          context,
          onClose,
        });
        return parcel.mountPromise;
      })
      .then(() => {
        if (parcel && !cancelled) {
          console.log('content-studio parcel mounted (React host)');
        }
      })
      .catch((err) => console.error('content-studio parcel error', err));

    return () => {
      cancelled = true;
      const current = parcel;
      if (current) {
        current.mountPromise
          .then(() => (current.getStatus() === 'MOUNTED' ? current.unmount() : undefined))
          .catch(() => undefined);
      }
    };
    // Mount once per overlay open; props are stable while the overlay is open.
  }, []);

  return (
    <div style={mountContainer} data-test-id="content-studio-overlay">
      <div ref={mountElRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
